/**
 * The small rules every part of the flow map model shares: ids, text in one or
 * many languages, safe links, rounded coordinates, and one key order for nodes,
 * arrows and documents so JSON compares stay stable. Moved out of model.ts as is.
 */
import { pickLang } from "@wa/core";
import {
  FLOW_DOC_VERSION,
  FLOW_LIMITS,
  type FlowEdge,
  type FlowMapDoc,
  type FlowNode,
  type FlowPoint,
  type FlowSize,
  type FlowText,
} from "../types.js";

export type Obj = Record<string, unknown>;

export const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);

export function oneOf<T extends string>(list: readonly T[], v: unknown): v is T {
  return typeof v === "string" && (list as readonly string[]).includes(v);
}

const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
export const LOCALE_KEY_RE = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})?$/;

// ---------------------------------------------------------------------------
// Ids
// ---------------------------------------------------------------------------

/** A document-local id: letters, digits, "_" and "-", at most 64 characters. */
export function isFlowId(v: unknown): v is string {
  return typeof v === "string" && ID_RE.test(v);
}

/**
 * A fresh id that is not in `taken`. The default is a deterministic counter
 * (`n1`, `n2` ...), which keeps a server render and the first client render
 * identical. Editors pass `random` so two people adding a node at the same
 * moment in two copies of a map do not mint the same id.
 */
export function makeFlowId(prefix: string, taken: ReadonlySet<string>, random = false): string {
  const p = prefix.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 16) || "x";
  if (random) {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (typeof c?.randomUUID === "function") {
      for (let i = 0; i < 8; i++) {
        const id = `${p}-${c.randomUUID().replace(/-/g, "").slice(0, 8)}`;
        if (!taken.has(id)) return id;
      }
    }
  }
  for (let n = taken.size + 1; ; n++) {
    const id = `${p}${n}`;
    if (!taken.has(id)) return id;
  }
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

/** The text to show for `t` in `locale`. Plain strings ignore the locale. */
export function readText(t: unknown, locale?: string): string {
  if (typeof t === "string") return t;
  if (isObj(t)) {
    const v = pickLang(t as Partial<Record<string, string>>, locale);
    return typeof v === "string" ? v : "";
  }
  return "";
}

/** True when `t` holds visible text in at least one language. */
export function hasText(t: unknown): boolean {
  if (typeof t === "string") return t.trim().length > 0;
  if (isObj(t)) return Object.values(t).some((v) => typeof v === "string" && v.trim().length > 0);
  return false;
}

/**
 * Write `value` into `prev`. Without a locale the result is a plain string. With
 * a locale the result is a per-locale map where only that locale changes; an
 * empty value removes the locale, and a map with no locale left becomes "".
 *
 * A plain-string `prev` carries no language tag, so `defaultLocale` says which
 * language it was written in and the old text is filed under it instead of
 * being thrown away. A map copied from the CRM holds plain strings: editing one
 * of them on the German tab used to leave `{ de: ... }` and nothing else, so
 * every other language fell back to the German text. Without `defaultLocale`
 * there is nowhere to file the old text and it is still replaced.
 */
export function writeText(
  prev: FlowText | undefined,
  value: string,
  locale?: string,
  defaultLocale?: string,
): FlowText {
  if (!locale) return value;
  const out: Record<string, string> = {};
  if (isObj(prev)) {
    for (const [k, v] of Object.entries(prev)) if (typeof v === "string") out[k] = v;
  } else if (typeof prev === "string" && prev && defaultLocale && defaultLocale !== locale) {
    out[defaultLocale] = prev;
  }
  if (value) out[locale] = value;
  else delete out[locale];
  return Object.keys(out).length ? out : "";
}

/** The one character Postgres `jsonb` cannot store. */
const NUL_RE = /\u0000/g;

/**
 * Text as the model keeps it: a plain string cut to `max`, a finite number as
 * its string, or a per-locale map with each valid language cut to `max`. An
 * empty language is dropped: `pickLang` skips only a missing language, so
 * `{ et: "x", de: "" }` would show a blank text on a German page instead of
 * falling back. A map with no language left is `undefined`.
 *
 * U+0000 is removed first. Postgres `jsonb` refuses it, so one pasted NUL
 * character made a CRM save fail with a 500 on every autosave (measured
 * against a real database on 2026-09-11).
 */
export function cleanText(v: unknown, max: number): FlowText | undefined {
  if (typeof v === "string") return v.replace(NUL_RE, "").slice(0, max);
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (isObj(v)) {
    const out: Record<string, string> = {};
    for (const [k, val] of Object.entries(v)) {
      if (!LOCALE_KEY_RE.test(k) || typeof val !== "string") continue;
      const cut = val.replace(NUL_RE, "").slice(0, max);
      if (cut) out[k] = cut;
    }
    return Object.keys(out).length ? out : undefined;
  }
  return undefined;
}

/** Optional text: kept only when it shows something. */
export function cleanOptionalText(v: unknown, max: number): FlowText | undefined {
  const t = cleanText(v, max);
  return t !== undefined && hasText(t) ? t : undefined;
}

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------

const SAFE_SCHEMES = new Set(["http", "https", "mailto", "tel"]);

/**
 * An href that is safe to render: an app path ("/x"), an anchor ("#x"), a query
 * ("?x"), or an http(s) / mailto / tel URL. No surrounding whitespace, no
 * protocol-relative "//host", no backslash, no control characters, no other
 * scheme (javascript:, data:, file: ...).
 */
export function isSafeHref(href: unknown): href is string {
  if (typeof href !== "string" || !href || href.length > FLOW_LIMITS.href) return false;
  if (href.trim() !== href) return false;
  for (let i = 0; i < href.length; i++) {
    const c = href.charCodeAt(i);
    if (c < 32 || c === 127) return false;
  }
  // A browser reads a backslash in a URL as a slash, so "/\evil.com" is really
  // "//evil.com": another host dressed up as an app path.
  if (href.includes("\\")) return false;
  if (href.startsWith("//")) return false;
  if (href.startsWith("/") || href.startsWith("#") || href.startsWith("?")) return true;
  const m = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(href);
  return !!m && SAFE_SCHEMES.has((m[1] ?? "").toLowerCase());
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

export const finite = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);

/** One decimal place, and never -0 (it would make two equal documents unequal in a deep compare). */
function round1(n: number): number {
  const r = Math.round(n * 10) / 10;
  return r === 0 ? 0 : r;
}

export function coord(n: number): number {
  return round1(Math.max(-FLOW_LIMITS.coord, Math.min(FLOW_LIMITS.coord, n)));
}

export function cleanPoint(v: unknown): FlowPoint | undefined {
  if (!isObj(v)) return undefined;
  const x = finite(v.x);
  const y = finite(v.y);
  return x === undefined || y === undefined ? undefined : { x: coord(x), y: coord(y) };
}

function sizeDim(n: number): number {
  return Math.round(Math.max(FLOW_LIMITS.sizeMin, Math.min(FLOW_LIMITS.sizeMax, n)));
}

export function cleanSize(v: unknown): FlowSize | undefined {
  if (!isObj(v)) return undefined;
  const w = finite(v.width);
  const h = finite(v.height);
  return w === undefined || h === undefined ? undefined : { width: sizeDim(w), height: sizeDim(h) };
}

// ---------------------------------------------------------------------------
// Canonical shapes (one key order everywhere, so JSON compares are stable)
// ---------------------------------------------------------------------------

export function canonicalNode(n: FlowNode): FlowNode {
  const out: FlowNode = { id: n.id, kind: n.kind, position: n.position, title: n.title };
  if (n.description !== undefined) out.description = n.description;
  if (n.tone !== undefined) out.tone = n.tone;
  if (n.steps !== undefined) out.steps = n.steps;
  if (n.links !== undefined) out.links = n.links;
  if (n.parentId !== undefined) out.parentId = n.parentId;
  if (n.size !== undefined) out.size = n.size;
  if (n.ref !== undefined) out.ref = n.ref;
  return out;
}

export function canonicalEdge(e: FlowEdge): FlowEdge {
  const out: FlowEdge = { id: e.id, source: e.source, target: e.target };
  if (e.sourceHandle !== undefined) out.sourceHandle = e.sourceHandle;
  if (e.targetHandle !== undefined) out.targetHandle = e.targetHandle;
  if (e.label !== undefined) out.label = e.label;
  if (e.kind !== undefined) out.kind = e.kind;
  return out;
}

export function canonicalDoc(d: FlowMapDoc): FlowMapDoc {
  const out = { version: FLOW_DOC_VERSION } as FlowMapDoc;
  if (d.direction !== undefined) out.direction = d.direction;
  if (d.edgeStyle !== undefined) out.edgeStyle = d.edgeStyle;
  out.nodes = d.nodes;
  out.edges = d.edges;
  return out;
}

/** Groups first (React Flow renders a parent before its children). Same array when already ordered. */
export function orderNodes(nodes: FlowNode[]): FlowNode[] {
  let seenOther = false;
  for (const n of nodes) {
    if (n.kind !== "group") seenOther = true;
    else if (seenOther) return [...nodes.filter((x) => x.kind === "group"), ...nodes.filter((x) => x.kind !== "group")];
  }
  return nodes;
}

export const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
